import { ADMIN_ROLE } from '@carsfinders/auth';
import { type DealQueueTab } from '@carsfinders/contracts';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api';
import { fetchBids as fetchAdminBids, fetchRelayQueue } from '../bids/api';
import { BIDS_QUERY_KEY, RELAY_QUEUE_QUERY_KEY } from '../bids/hooks/use-bid-mutations';
import { type RelayQueueItem } from '../bids/types';
import { fetchAuctionSchedule } from '../auction-schedule/api';
import { type AuctionScheduleItem } from '../auction-schedule/types';
import { fetchUsers } from '../clients/api';
import { fetchDealerApplications } from '../dealer/api';
import { fetchDocuments } from '../documents/api';
import { isSuperadmin } from '../auth/use-session-roles';
import { fetchAdminListings } from '../marketplace/api';
import { fetchWonCars } from '../won-cars/api';
import { WonCarDeliveryStatus } from '../won-cars/types';
import { WON_CARS_QUERY_KEY } from '../won-cars/hooks/use-update-won-car';
import {
  countAwaitingVerification,
  countLiveBids,
  countOverdueRelay,
  openRelayRows,
} from './metrics';

/**
 * The Overview screen's data — the seven tiles the `Redesign-D-Admin` artboard
 * draws, plus the two sections under them (the relay queue and today's closings).
 *
 * Nothing here is a new endpoint: every number is a count over a list some admin
 * surface already serves, which is what lets each tile link to the screen that
 * resolves it. Query keys are the SHARED constants where one exists, so an action
 * taken on a feature page invalidates the matching tile, and opening that page
 * re-uses the fetch this screen already made.
 */

/** A single tile. `value` is undefined while loading or after an error. */
export interface DashboardMetric {
  /** i18n suffix under `admin.dashboard.tiles.*`. */
  key: string;
  /** Route the tile links to — the surface that resolves this queue. */
  to: string;
  /** The «Угоди» tab the tile opens, when `to` is `/deals`. */
  tab?: DealQueueTab;
  value: number | undefined;
  /**
   * Resolved secondary line. Always a literal: `t()` echoes the raw `{token}`
   * when an interpolation param is absent, so the caller formats before us.
   */
  sub: string;
  /**
   * The mobile artboard writes every tile shorter — «Ретрансляція», not «Черга
   * ретрансляції» — because two columns at 390px cannot hold the desktop
   * wording without wrapping to three lines. Same number, tighter words.
   */
  subShort: string;
  /**
   * False for the one tile the mobile artboard leaves out. Six tiles fit the
   * screen; the seventh pushed the relay queue below the fold, and the queue is
   * the reason the operator opened the screen.
   */
  onMobile: boolean;
  /**
   * The artboard's 3px top rule. `danger` marks a queue that is late, `accent` a
   * queue waiting on a decision, `info` the logistics tile, `line` the rest —
   * the same four-signal legend `Redesign-D-Overview` states.
   */
  tone: 'line' | 'danger' | 'accent' | 'info';
  isLoading: boolean;
  isError: boolean;
}

export interface DashboardData {
  metrics: DashboardMetric[];
  relay: { rows: RelayQueueItem[]; overdue: number; isLoading: boolean; isError: boolean };
  today: { rows: AuctionScheduleItem[]; isLoading: boolean; isError: boolean };
  /** Any query answered 401/403 — the page renders <AccessDenied /> instead. */
  isForbidden: boolean;
  isFetching: boolean;
  refetchAll: () => void;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

export function useDashboardMetrics(
  t: Translate,
  roles: string[] = [ADMIN_ROLE],
): DashboardData {
  const queryClient = useQueryClient();
  /*
   * «Маркетплейс» is a superuser screen (owner decision 2026-10-13). For a
   * plain admin the moderation count is neither fetched — its 403 would trip
   * `isForbidden` and blank the whole Overview — nor drawn as a tile.
   */
  const canModerate = isSuperadmin(roles);

  const results = useQueries({
    queries: [
      { queryKey: RELAY_QUEUE_QUERY_KEY, queryFn: fetchRelayQueue },
      { queryKey: ['admin-documents', 'pending'], queryFn: () => fetchDocuments('pending') },
      { queryKey: ['clients'], queryFn: fetchUsers },
      { queryKey: BIDS_QUERY_KEY, queryFn: fetchAdminBids },
      { queryKey: WON_CARS_QUERY_KEY, queryFn: fetchWonCars },
      {
        queryKey: ['dealer-applications', 'pending'],
        queryFn: () => fetchDealerApplications('pending'),
      },
      {
        queryKey: ['admin-marketplace-listings', 'moderation'],
        // limit 1: only `total` is read here, so do not pull a page of rows.
        queryFn: () => fetchAdminListings({ status: 'moderation', limit: 1 }),
        enabled: canModerate,
      },
      {
        queryKey: ['admin-auction-schedule', 'today'],
        queryFn: () => fetchAuctionSchedule({ filters: { timeframe: 'today' }, limit: 6 }),
      },
    ],
  });

  const [relay, documents, clients, bids, wonCars, dealers, marketplace, schedule] = results;

  const relayRows = relay?.data ?? [];
  const openRelay = openRelayRows(relayRows);
  const overdueRelay = countOverdueRelay(relayRows);

  const users = clients?.data ?? [];
  const unverified = countAwaitingVerification(users);

  const won = wonCars?.data ?? [];
  const inTransit = won.filter((car) => car.status === WonCarDeliveryStatus.InTransit).length;
  const atCustoms = won.filter((car) => car.status === WonCarDeliveryStatus.AtCustoms).length;

  const tile = (
    key: string,
    to: string,
    query: { isLoading: boolean; isError: boolean } | undefined,
    value: number,
    sub: string,
    options: {
      tone?: DashboardMetric['tone'];
      subShort?: string;
      onMobile?: boolean;
      tab?: DealQueueTab;
    } = {},
  ): DashboardMetric => {
    const loading = query?.isLoading ?? true;
    const failed = query?.isError ?? false;
    const failedText = t('admin.dashboard.failed');
    return {
      key,
      to,
      ...(options.tab ? { tab: options.tab } : {}),
      value: loading || failed ? undefined : value,
      sub: failed ? failedText : sub,
      subShort: failed ? failedText : (options.subShort ?? sub),
      onMobile: options.onMobile ?? true,
      tone: failed ? 'line' : (options.tone ?? 'line'),
      isLoading: loading,
      isError: failed,
    };
  };

  const metrics: DashboardMetric[] = [
    tile(
      'relay',
      '/deals',
      relay,
      openRelay.length,
      t('admin.dashboard.tiles.relay.sub'),
      {
        // The one tile whose tone is earned rather than fixed: a non-empty relay
        // queue is the state the whole business model depends on clearing.
        tone: openRelay.length > 0 ? 'danger' : 'line',
        subShort: t('admin.dashboard.tiles.relay.subShort'),
        tab: 'relay',
      },
    ),
    tile(
      'documents',
      '/documents',
      documents,
      documents?.data?.pagination.total ?? 0,
      t('admin.dashboard.tiles.documents.sub'),
      { tone: 'accent', subShort: t('admin.dashboard.tiles.documents.subShort') },
    ),
    tile(
      'clients',
      '/clients',
      clients,
      unverified,
      t('admin.dashboard.tiles.clients.sub', { total: users.length }),
      { subShort: t('admin.dashboard.tiles.clients.subShort', { total: users.length }) },
    ),
    tile(
      'bids',
      '/deals',
      bids,
      countLiveBids(bids?.data ?? []),
      t('admin.dashboard.tiles.bids.sub'),
      { subShort: t('admin.dashboard.tiles.bids.subShort'), tab: 'leading' },
    ),
    tile(
      'wonCars',
      '/deals',
      wonCars,
      won.length,
      inTransit + atCustoms > 0
        ? t('admin.dashboard.tiles.wonCars.subFlow', { transit: inTransit, customs: atCustoms })
        : t('admin.dashboard.tiles.wonCars.sub'),
      { tone: 'info', subShort: t('admin.dashboard.tiles.wonCars.subShort'), tab: 'shipping' },
    ),
    tile(
      'dealers',
      '/dealer-applications',
      dealers,
      dealers?.data?.applications.length ?? 0,
      t('admin.dashboard.tiles.dealers.sub'),
      { tone: 'accent', subShort: t('admin.dashboard.tiles.dealers.subShort') },
    ),
    ...(canModerate
      ? [
          tile(
            'marketplace',
            '/marketplace',
            marketplace,
            marketplace?.data?.total ?? 0,
            t('admin.dashboard.tiles.marketplace.sub'),
            { subShort: t('admin.dashboard.tiles.marketplace.subShort'), onMobile: false },
          ),
        ]
      : []),
  ];

  return {
    metrics,
    relay: {
      rows: openRelay,
      overdue: overdueRelay,
      isLoading: relay?.isLoading ?? true,
      isError: relay?.isError ?? false,
    },
    today: {
      rows: schedule?.data?.data ?? [],
      isLoading: schedule?.isLoading ?? true,
      isError: schedule?.isError ?? false,
    },
    isForbidden: results.some(
      (result) =>
        result.error instanceof ApiError &&
        (result.error.status === 401 || result.error.status === 403),
    ),
    isFetching: results.some((result) => result.isFetching),
    refetchAll: () => {
      for (const key of [
        RELAY_QUEUE_QUERY_KEY,
        ['admin-documents'],
        ['clients'],
        BIDS_QUERY_KEY,
        WON_CARS_QUERY_KEY,
        ['dealer-applications'],
        ['admin-marketplace-listings'],
        ['admin-auction-schedule'],
      ]) {
        void queryClient.invalidateQueries({ queryKey: key as readonly unknown[] });
      }
    },
  };
}
